/**
 * Mini chart subcomponents for the ColumnProfileBadge popover:
 * - MiniHistogram: tiny SVG bar chart (numeric/date columns)
 * - MiniTopN: top-N horizontal bars (categorical or low-distinct numeric)
 */

import React from 'react';
import type { HistogramBucket } from '../utils/columnHistogram';
import type { TopNEntry } from '../utils/columnTopN';

const HIST_WIDTH = 240;
const HIST_HEIGHT = 40;

type MiniHistogramProps = {
  buckets: HistogramBucket[];
};

export const MiniHistogram = React.memo(function MiniHistogram({ buckets }: MiniHistogramProps) {
  if (buckets.length === 0) return null;
  const maxCount = buckets.reduce((m, b) => (b.count > m ? b.count : m), 0);
  if (maxCount === 0) return null;

  const barGap = 1;
  const barWidth = (HIST_WIDTH - (buckets.length - 1) * barGap) / buckets.length;

  return (
    <svg
      width={HIST_WIDTH}
      height={HIST_HEIGHT}
      viewBox={`0 0 ${HIST_WIDTH} ${HIST_HEIGHT}`}
      className="text-blue-500 fill-current"
      data-testid="column-profile-histogram"
      role="img"
      aria-label="Column value distribution"
    >
      {buckets.map((b, i) => {
        const h = Math.max(1, Math.round((b.count / maxCount) * (HIST_HEIGHT - 2)));
        const x = i * (barWidth + barGap);
        const y = HIST_HEIGHT - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={h}
            rx={1}
            opacity={0.8}
          >
            <title>
              {String(b.rangeStart)} – {String(b.rangeEnd)}: {b.count.toLocaleString()}
            </title>
          </rect>
        );
      })}
    </svg>
  );
});

type MiniTopNProps = {
  entries: TopNEntry[];
};

export const MiniTopN = React.memo(function MiniTopN({ entries }: MiniTopNProps) {
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-col gap-1" data-testid="column-profile-topn">
      {entries.map((e, i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span
              className="truncate max-w-[60%] font-medium"
              title={e.displayValue || '(empty)'}
            >
              {e.displayValue || <span className="text-muted-foreground italic">(empty)</span>}
            </span>
            <span className="text-muted-foreground tabular-nums shrink-0">
              {e.count.toLocaleString()} ({e.percent.toFixed(1)}%)
            </span>
          </div>
          <div className="h-1 w-full rounded-full bg-muted/60 overflow-hidden">
            <div
              className="h-full bg-blue-500/70 rounded-full"
              style={{ width: `${Math.max(2, Math.min(100, e.percent))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
});
